import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  PlusIcon,
  TrashIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  CogIcon,
  TableCellsIcon,
  UserIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { campaignService } from "../../services/campaignService";
import { adminService } from "../../services/adminService";
import { scriptService } from "../../services/scriptService";
import { smsService } from "../../services/smsService";
import { useAuth } from "../../contexts/AuthContext";
import {
  BOT_ENGINES,
  Campaign,
  CaptureField,
  Voice,
  AssistantConfig,
  PhoneNumber,
  ConversationStyle,
  isBotEngine,
  conversationStylesVoiceError,
} from "../../types/campaign";
import { AdminUser } from "../../types/admin";
import { Script } from "../../types/script";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import { formatApiErrorMessage } from "../../utils/formatApiError";
import { CAMPAIGN_TIMEZONE_OPTIONS } from "../../constants/campaignTimezones";

import SmsCampaignSetupFields from "../../components/Campaign/SmsCampaignSetupFields";
import DialScheduleFields from "../../components/Campaign/DialScheduleFields";
import { ConversationStylesManager } from "../../components/Campaign/ConversationStylesManager";
import PlaceholderHelper from "../../components/Campaign/PlaceholderHelper";
import TelnyxAssistantPicker from "../../components/Campaign/TelnyxAssistantPicker";
import PhoneNumberList from "../../components/Campaign/PhoneNumberList";
import Button from "../../components/UI/Button";
import Switch from "../../components/UI/Switch";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/UI/Card";
import { SectionHeader } from "../../components/UI/SectionHeader";
import { toast } from "react-hot-toast";
import Breadcrumb from "../../components/UI/Breadcrumb";
import ErrorMessage from "../../components/UI/ErrorMessage";
import CampaignSideNav from "../../components/Campaign/CampaignSideNav";

const updateCampaignSchema = z
  .object({
    name: z
      .string()
      .min(1, "Campaign name is required")
      .max(100, "Name must be less than 100 characters"),
    callDescription: z
      .string()
      .max(500, "Description must be less than 500 characters")
      .optional()
      .default(""),
    campaignType: z.enum(["bot", "human", "text"]).default("human"),
    botEngine: z.enum(BOT_ENGINES).default("telnyx_assistant"),
    botPipelineV2: z.boolean().optional().default(false),
    telnyxAssistantId: z.string().optional(),
    dialingType: z.enum(["auto", "manual"]).default("auto"),
    agentIds: z.array(z.string()).optional(),
    phoneNumberIds: z.array(z.string()).optional().default([]),
    textTemplateBody: z.string().optional(),
    warmupStartVolume: z.number().min(1).max(5000).optional(),
    warmupEndVolume: z.number().min(1).max(10000).optional(),
    warmupDays: z.number().min(1).max(90).optional(),
    quietHoursStart: z.string().optional(),
    quietHoursEnd: z.string().optional(),
    sendSchedules: z.array(z.object({
      startTime: z.string(),
      endTime: z.string(),
      timezone: z.string(),
    })).optional().default([]),
    voiceDialSchedules: z.array(z.object({
      startTime: z.string(),
      endTime: z.string(),
      timezone: z.string(),
    })).optional().default([]),
    sequenceSteps: z
      .array(
        z.object({
          stepNumber: z.number().min(1),
          templateBody: z.string().min(1),
          delayHours: z.number().min(0).max(720),
        }),
      )
      .optional(),
    eligibleLeadStatuses: z.array(z.string()).optional().default([]),
    scriptId: z
      .union([z.string().uuid(), z.literal(""), z.null()])
      .optional()
      .transform((value) => (value === "" ? null : value)),
    promptScript: z.string().optional(),
    interestedQuestions: z.string().optional(),

    assistantFirstMessage: z.string().optional(),
    assistantFirstMessageMode: z
      .enum(["assistant-speaks-first", "assistant-waits-for-user"])
      .optional()
      .default("assistant-speaks-first"),
    // Conversation parameters
    conversationStyle: z.string().optional(),
    personalityType: z.string().optional(),
    persuasionApproach: z.string().optional(),
    conversationStyles: z
      .array(
        z.object({
          id: z.string().uuid().optional(),
          name: z.string(),
          conversationStyle: z.enum(["natural", "professional", "casual"]),
          personalityType: z.enum(["curious", "helpful", "friendly"]),
          persuasionApproach: z.enum(["gentle", "consultative", "direct"]),
          transcriber: z
            .enum([
              "deepgram",
              "11labs",
              "assembly-ai",
              "azure",
              "gladia",
              "google",
              "openai",
            ])
            .optional(),
          isActive: z.boolean(),
          enhancedPromptScript: z
            .string()
            .min(1, "Conversation style prompt is required"),
          selectedVoiceId: z
            .string()
            .nullish()
            .transform((val) => {
              const trimmed = String(val || "").trim();
              return trimmed || undefined;
            }),
          voiceConfig: z
            .union([
              z.string(),
              z.object({}).passthrough(),
              z.null(),
              z.undefined(),
            ])
            .transform((val) => {
              if (val === null || val === undefined) return undefined;
              if (typeof val === "string") {
                if (val.trim() === "") return undefined;
                return val;
              }
              if (typeof val === "object") {
                // Convert existing JSON object to string for new format
                return JSON.stringify(val);
              }
              return undefined;
            })
            .optional(),
        }),
      )
      .optional(),
    timezone: z.string().optional().default("America/New_York"),
    timezonePrioritizationEnabled: z.boolean().optional().default(false),
    areaCodePriorityEnabled: z.boolean().optional().default(false),
    callsPerAgent: z.union([z.number().int().min(1).max(10), z.nan(), z.null()]).optional().nullable(),
    dialRatio: z.union([z.number().min(1).max(2.5), z.nan(), z.null()]).optional().nullable(),
    dialRatioMin: z.union([z.number().min(1).max(2), z.nan(), z.null()]).optional().nullable(),
    dialRatioMax: z.union([z.number().min(1).max(2.5), z.nan(), z.null()]).optional().nullable(),
    botDialingMode: z.enum(['legacy', 'capacity']).optional().default('legacy'),
    botTargetActiveConversations: z.union([z.number().int().min(1).max(100), z.nan()]).optional(),
    botMaxActiveConversations: z.union([z.number().int().min(1).max(150), z.nan()]).optional(),
    botWaveSize: z.union([z.number().int().min(1).max(100), z.nan()]).optional(),
    botWaveIntervalMs: z.union([z.number().int().min(500).max(10000), z.nan()]).optional(),
    leadShuffleEnabled: z.boolean().optional().default(false),
    dialTimezoneFilter: z
      .enum(['auto', 'Eastern', 'Central', 'Mountain', 'Pacific', 'Unknown'])
      .optional()
      .default('auto'),
    captureSchema: z
      .array(
        z.object({
          name: z.string().min(1, "Field name is required"),
          label: z.string().min(1, "Field label is required"),
          type: z
            .enum([
              "string",
              "number",
              "email",
              "phone",
              "select",
              "multiselect",
              "boolean",
            ])
            .or(z.literal("text").transform(() => "string" as const)),
          required: z.boolean(),
          placeholder: z.string().optional(),
          options: z.array(z.string()).optional(),
        }),
      )
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.campaignType === "bot") {
      if (data.botEngine === "telnyx_assistant") {
        if (!data.telnyxAssistantId || !data.telnyxAssistantId.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["telnyxAssistantId"],
            message: "Telnyx Assistant ID is required for Telnyx AI Assistant",
          });
        }
        if (!data.promptScript || data.promptScript.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["promptScript"],
            message: "Script is required",
          });
        }
        if (
          !data.assistantFirstMessage ||
          data.assistantFirstMessage.trim().length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["assistantFirstMessage"],
            message: "First Message (Greetings) is required",
          });
        }
      } else if (
        data.botEngine === "conversation_relay" ||
        // Self-hosted builds its prompt from the same script and greeting, so
        // it needs them exactly as much as the relay engine does.
        data.botEngine === "self_hosted"
      ) {
      if (!data.promptScript || data.promptScript.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["promptScript"],
          message: "Script is required",
        });
      }
      if (
        !data.assistantFirstMessage ||
        data.assistantFirstMessage.trim().length === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assistantFirstMessage"],
          message: "First Message (Greetings) is required",
        });
      }
      // Conversation styles are required for bot campaigns
      if (!data.conversationStyles || data.conversationStyles.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["conversationStyles"],
          message: "At least one conversation style is required",
        });
      } else if (
        !data.conversationStyles.some(
          (style) =>
            style.enhancedPromptScript &&
            style.enhancedPromptScript.trim().length > 0,
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["conversationStyles"],
          message: "At least one conversation style must have a prompt",
        });
      } else if (
        data.conversationStyles.some(
          (style) => style.isActive && !(style.selectedVoiceId || "").trim(),
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["conversationStyles"],
          message:
            "Each active conversation style needs a voice (ElevenLabs or Fish Audio on self-hosted)",
        });
      } else {
        const voiceProviderError = conversationStylesVoiceError(
          data.botEngine,
          data.conversationStyles,
        );
        if (voiceProviderError) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["conversationStyles"],
            message: voiceProviderError,
          });
        }
      }
      } else if (
        data.botEngine === "vapi_direct" ||
        data.botEngine === "vapi_bridge"
      ) {
        if (!data.promptScript || data.promptScript.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["promptScript"],
            message: "Script is required",
          });
        }
        if (
          !data.assistantFirstMessage ||
          data.assistantFirstMessage.trim().length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["assistantFirstMessage"],
            message: "First Message (Greetings) is required",
          });
        }
        if (!data.conversationStyles || data.conversationStyles.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["conversationStyles"],
            message: "At least one conversation style is required",
          });
        } else if (
          !data.conversationStyles.some(
            (style) =>
              style.enhancedPromptScript &&
              style.enhancedPromptScript.trim().length > 0,
          )
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["conversationStyles"],
            message: "At least one conversation style must have a prompt",
          });
        } else if (
          data.conversationStyles.some(
            (style) => style.isActive && !(style.selectedVoiceId || "").trim(),
          )
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["conversationStyles"],
            message: "Each active conversation style needs a voice",
          });
        } else {
          const voiceProviderError = conversationStylesVoiceError(
            data.botEngine,
            data.conversationStyles,
          );
          if (voiceProviderError) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["conversationStyles"],
              message: voiceProviderError,
            });
          }
        }
        if (
          data.botEngine === "vapi_bridge" &&
          (!data.phoneNumberIds || data.phoneNumberIds.length === 0)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["phoneNumberIds"],
            message:
              "Select at least one Telnyx outbound number for bridge dialing",
          });
        }
      }
    } else if (data.campaignType === "human") {
      // Human campaigns only require name — no bot-specific fields needed
    } else if (data.campaignType === "text") {
      if (!data.sequenceSteps || data.sequenceSteps.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sequenceSteps"],
          message: "Add at least one message in the sequence",
        });
      } else {
        data.sequenceSteps.forEach((step, i) => {
          if (!step.templateBody || step.templateBody.trim().length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["sequenceSteps"],
              message: i === 0 ? "Message 1 text is required" : `Follow-up ${i} message text is required`,
            });
          }
        });
      }
      if (!data.phoneNumberIds || data.phoneNumberIds.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["phoneNumberIds"],
          message: "Select at least one SMS-capable number",
        });
      }
      if (!data.eligibleLeadStatuses || data.eligibleLeadStatuses.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["eligibleLeadStatuses"],
          message: "SMS allow-list required: select at least one lead status that should receive automated SMS.",
        });
      }
      data.sendSchedules?.forEach((slot, index) => {
        if (!slot.startTime?.trim() || !slot.endTime?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["sendSchedules"],
            message: `Send window ${index + 1}: start and end time are required`,
          });
        } else if (slot.startTime === slot.endTime) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["sendSchedules"],
            message: `Send window ${index + 1}: start and end time cannot be the same`,
          });
        }
      });
    }
    if (data.campaignType === "bot" || (data.campaignType === "human" && data.dialingType === "auto")) {
      data.voiceDialSchedules?.forEach((slot, index) => {
        if (!slot.startTime?.trim() || !slot.endTime?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["voiceDialSchedules"],
            message: `Dial window ${index + 1}: start and end time are required`,
          });
        } else if (slot.startTime === slot.endTime) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["voiceDialSchedules"],
            message: `Dial window ${index + 1}: start and end time cannot be the same`,
          });
        }
      });
    }
  });

// Minimal schema for non-admin users (name-only edits)
const minimalUpdateSchema = z.object({
  name: z
    .string()
    .min(1, "Campaign name is required")
    .max(100, "Name must be less than 100 characters"),
});

type UpdateCampaignFormData = z.infer<typeof updateCampaignSchema>;

const EditCampaignPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(true);
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  // Treat organization/member role as restricted for editing
  const isRestricted = user?.role === "member";
  const [agents, setAgents] = useState<AdminUser[]>([]);
  const [scripts, setScripts] = useState<Script[]>([]);
  const [loadingScripts, setLoadingScripts] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');

  // Transfer ownership state
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [transferLoading, setTransferLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Active section state for tab-like functionality
  const [activeSection, setActiveSection] = useState<string>(
    searchParams.get("section") || "basic-info"
  );

  const isAdmin = user?.role === "admin";
  const isOwner = campaign && user && campaign.ownerId === user.id;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<UpdateCampaignFormData>({
    resolver: zodResolver(
      user?.role === "admin" ? updateCampaignSchema : minimalUpdateSchema,
    ),
  });

  const captureSchema = watch("captureSchema") || [];
  const safeCaptureSchema = Array.isArray(captureSchema) ? captureSchema : [];
  const campaignType = watch("campaignType");
  const botEngine = watch("botEngine");
  const isHumanCampaign = campaignType === "human";
  const isBotCampaign = campaignType === "bot";
  const isTextCampaign = campaignType === "text";
  const isTelnyxAssistantBot = isBotCampaign && botEngine === "telnyx_assistant";
  const isVapiDirectBot = isBotCampaign && botEngine === "vapi_direct";
  const isVapiBridgeBot = isBotCampaign && botEngine === "vapi_bridge";
  const isConversationRelayBot = isBotCampaign && botEngine === "conversation_relay";
  const sequenceSteps = watch("sequenceSteps") || [];
  const eligibleLeadStatuses = watch("eligibleLeadStatuses") || [];

  useEffect(() => {
    if (!isTextCampaign) return;
    const first = sequenceSteps[0]?.templateBody ?? "";
    setValue("textTemplateBody", first);
    setValue("dialingType", "auto");
  }, [isTextCampaign, sequenceSteps, setValue]);

  // Telnyx numbers for bridge / SMS pickers (Vapi Direct uses Manage Numbers pool — no picker)
  const [phoneLoadError, setPhoneLoadError] = useState<string | null>(null);

  const displayPhoneNumbers = React.useMemo(() => {
    return phoneNumbers.map((p) => {
      const localId = (p as PhoneNumber & { localPhoneNumberId?: string }).localPhoneNumberId;
      const agent = agents.find(
        (a) => a.phone_number_id === p.id || (localId && a.phone_number_id === localId)
      );
      if (agent) {
        return {
          ...p,
          name: `${agent.name} (${agent.email}) — ${p.number}`,
        };
      }
      return p;
    });
  }, [phoneNumbers, agents]);

  // Load Telnyx phone numbers when bridge/SMS pickers need them
  useEffect(() => {
    const loadData = async () => {
      try {
        if (isAdmin && !isVapiDirectBot) {
          setLoadingPhoneNumbers(true);
          setPhoneLoadError(null);
          const phoneNums = await campaignService.getPhoneNumbers();
          setPhoneNumbers(phoneNums);
          if (phoneNums.length === 0) {
            setPhoneLoadError('No numbers returned from Telnyx. Check API key / connection in provider settings.');
          }
        }
      } catch (error: any) {
        console.error("Failed to load data:", error);
        if (isAdmin) {
          const msg =
            error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            error?.message ||
            'Failed to load Telnyx phone numbers';
          setPhoneLoadError(msg);
          setPhoneNumbers([]);
          toast.error(msg);
        }
      } finally {
        if (isAdmin) {
          setLoadingPhoneNumbers(false);
        } else {
          setLoadingPhoneNumbers(false);
          setPhoneNumbers([]);
        }
      }
    };

    loadData();
  }, [isAdmin, isVapiDirectBot]);

  useEffect(() => {
    const loadAgents = async () => {
      // Always load agents to filter phone numbers
      setLoadingAgents(true);
      try {
        const response = await adminService.getUsers({
          role: "agent",
          page: 1,
          limit: 100,
        });
        const list = response?.data?.users ?? [];
        setAgents(list);
      } catch (error) {
        console.error("Failed to load agents:", error);
        toast.error("Failed to load agents");
      } finally {
        setLoadingAgents(false);
      }
    };

    loadAgents();
  }, []); // Run once on mount

  useEffect(() => {
    const loadScripts = async () => {
      setLoadingScripts(true);
      try {
        // Keep inactive scripts too so an already-assigned inactive script still appears in the select
        const data = await scriptService.getScripts();
        setScripts(data);
      } catch {
        toast.error("Failed to load scripts");
      } finally {
        setLoadingScripts(false);
      }
    };

    loadScripts();
  }, []);

  // Re-apply assigned script after options load so the <select> doesn't get stuck on ""
  // (uncontrolled select wipes the value when options mount late).
  useEffect(() => {
    if (loadingScripts || !campaign) return;
    setValue("scriptId", campaign.scriptId ?? null, { shouldDirty: false });
  }, [loadingScripts, campaign?.id, campaign?.scriptId, setValue]);

  useEffect(() => {
    if (
      !isBotCampaign &&
      ["conversation-styles", "script-config", "data-capture"].includes(
        activeSection,
      )
    ) {
      setActiveSection("basic-info");
    }
  }, [isBotCampaign, activeSection]);

  useEffect(() => {
    if (id) {
      loadCampaignData();
    }
  }, [id]);

  const loadCampaignData = async () => {
    try {
      setPageLoading(true);
      const campaignData = await campaignService.getCampaign(id!);
      setCampaign(campaignData);
      const fetchedSequenceSteps =
        campaignData.campaignType === "text"
          ? await smsService
              .getCampaignSequenceSteps(campaignData.id)
              .catch(() => [])
          : [];
      const normalizedSequenceSteps = fetchedSequenceSteps
        .map((step: any) => ({
          stepNumber: step.step_number ?? step.stepNumber,
          templateBody: step.template_body ?? step.templateBody,
          delayHours: step.delay_hours ?? step.delayHours ?? 0,
        }))
        .filter(
          (step: any) =>
            Number.isFinite(Number(step.stepNumber)) &&
            String(step.templateBody || "").trim().length > 0,
        );

      console.log("Loaded campaign data:", campaignData);

      // Populate form with campaign data
      const formData = {
        name: campaignData.name,
        callDescription: campaignData.callDescription,
        promptScript: campaignData.promptScript,
        interestedQuestions: (campaignData.interestedQuestions || []).join(
          "\n",
        ),
        assistantFirstMessage: campaignData.assistantFirstMessage || "",
        assistantFirstMessageMode: (campaignData.assistantFirstMessageMode ===
        "assistant-waits-for-user"
          ? "assistant-waits-for-user"
          : "assistant-speaks-first") as
          | "assistant-speaks-first"
          | "assistant-waits-for-user",
        // Ensure campaignType is a valid value
        campaignType:
          campaignData.campaignType === "bot" ||
          campaignData.campaignType === "human" ||
          campaignData.campaignType === "text"
            ? campaignData.campaignType
            : "human",
        // Falling back here silently rewrites what the campaign is actually set
        // to, and the next Update would save that rewrite over the real value —
        // so the check has to come from the same list the form offers.
        botEngine: isBotEngine(campaignData.botEngine)
          ? campaignData.botEngine
          : "telnyx_assistant",
        botPipelineV2: Boolean(campaignData.botPipelineV2),
        telnyxAssistantId: campaignData.telnyxAssistantId || "",
        dialingType:
          campaignData.dialingType === "auto" ||
          campaignData.dialingType === "manual"
            ? campaignData.dialingType
            : "auto",
        agentIds: campaignData.agentIds || [],
        scriptId: campaignData.scriptId || null,
        phoneNumberIds: campaignData.phoneNumberIds || [],
        textTemplateBody: campaignData.textConfig?.templateBody || "",
        warmupStartVolume: campaignData.textConfig?.warmupStartVolume || 25,
        warmupEndVolume: campaignData.textConfig?.warmupEndVolume || 250,
        warmupDays: campaignData.textConfig?.warmupDays || 14,
        eligibleLeadStatuses: campaignData.textConfig?.eligibleLeadStatuses || [],
        sendSchedules: campaignData.textConfig?.sendSchedules || [],
        voiceDialSchedules: campaignData.voiceDialSchedules || [],
        quietHoursStart: campaignData.quietHoursStart || '',
        quietHoursEnd: campaignData.quietHoursEnd || '',
        sequenceSteps:
          normalizedSequenceSteps.length > 0
            ? normalizedSequenceSteps
            : campaignData.campaignType === "text"
              ? [
                  {
                    stepNumber: 1,
                    templateBody: campaignData.textConfig?.templateBody || "",
                    delayHours: 0,
                  },
                ]
              : [],
        personalityType: campaignData.personalityType || "",
        persuasionApproach: campaignData.persuasionApproach || "",
        conversationStyles: (campaignData.conversationStyles || []).map((style: any) => ({
          ...style,
          selectedVoiceId: style.selectedVoiceId ?? undefined,
        })),
        timezone: campaignData.timezone,
        timezonePrioritizationEnabled: campaignData.timezonePrioritizationEnabled || false,
        areaCodePriorityEnabled: campaignData.areaCodePriorityEnabled || false,
        callsPerAgent: campaignData.callsPerAgent ?? null,
        dialRatio: campaignData.dialRatio ?? 1.3,
        dialRatioMin: campaignData.dialRatioMin ?? 1.0,
        dialRatioMax: campaignData.dialRatioMax ?? 2.5,
        botDialingMode:
          campaignData.botDialingMode === 'capacity'
            ? ('capacity' as const)
            : ('legacy' as const),
        botTargetActiveConversations: campaignData.botTargetActiveConversations ?? 10,
        botMaxActiveConversations: campaignData.botMaxActiveConversations ?? 12,
        botWaveSize: campaignData.botWaveSize ?? 25,
        botWaveIntervalMs: campaignData.botWaveIntervalMs ?? 1500,
        leadShuffleEnabled: campaignData.leadShuffleEnabled || false,
        dialTimezoneFilter: (campaignData.dialTimezoneFilter as 'auto' | 'Eastern' | 'Central' | 'Mountain' | 'Pacific' | 'Unknown') || 'auto',
        captureSchema: (Array.isArray(campaignData.captureSchema) ? campaignData.captureSchema : []).map(
          (field: any) => ({
            ...field,
            type: field.type === 'text' ? (field.name === 'email' ? 'email' : 'string') : field.type,
          })
        ),
      };

      console.log("Form data being set:", formData);
      reset(formData);

      // Validate the form data after loading
      setTimeout(() => {
        console.log("Current form values after reset:", watch());
        console.log("Current form errors after reset:", errors);
      }, 100);
    } catch (error: any) {
      console.error("Failed to load campaign data:", error);

      // Extract specific error message from backend response
      let errorMessage = "Failed to load campaign data";
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setPageLoading(false);
    }
  };

  // Load users for transfer ownership dropdown
  const loadUsers = async () => {
    if (!isAdmin && !isOwner) return;

    try {
      setLoadingUsers(true);
      const usersResponse = await adminService.getUsers({ limit: 1000 });

      // Check if response has the expected structure
      if (
        !usersResponse ||
        !usersResponse.data ||
        !usersResponse.data.users ||
        !Array.isArray(usersResponse.data.users)
      ) {
        console.error("Invalid users response structure:", usersResponse);
        toast.error("Invalid response from server");
        return;
      }

      // Filter out the current owner from the list
      const filteredUsers = usersResponse.data.users.filter(
        (u: AdminUser) => u.id !== campaign?.ownerId,
      );
      setUsers(filteredUsers);
    } catch (error) {
      console.error("Failed to load users:", error);
      toast.error(
        `Failed to load users: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    } finally {
      setLoadingUsers(false);
    }
  };

  // Handle transfer ownership
  const handleTransferOwnership = async () => {
    if (!selectedUserId || !campaign) {
      toast.error("Please select a user to transfer ownership to");
      return;
    }

    try {
      setTransferLoading(true);
      await campaignService.transferOwnership(campaign.id, selectedUserId);
      toast.success("Campaign ownership transferred successfully");

      // Reload campaign data to reflect the change
      await loadCampaignData();
      setSelectedUserId("");
    } catch (error: any) {
      console.error("Failed to transfer ownership:", error);
      let errorMessage = "Failed to transfer ownership";
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setTransferLoading(false);
    }
  };

  // Load users when campaign data is loaded and user has permission
  useEffect(() => {
    if (campaign && (isAdmin || isOwner)) {
      loadUsers();
    }
  }, [campaign, isAdmin, isOwner]);

  const scrollToError = () => {
    // Find the first error field and scroll to it
    const errorFields = isBotCampaign
      ? ["name", "callDescription", "promptScript", "assistantFirstMessage"]
      : isTextCampaign
        ? ["name", "callDescription", "agentIds", "phoneNumberIds", "sequenceSteps", "eligibleLeadStatuses"]
        : ["name", "callDescription", "agentIds"];

    for (const fieldName of errorFields) {
      if (errors[fieldName as keyof typeof errors]) {
        // Find the element by looking for the error message or field container
        let targetElement: HTMLElement | null = null;

        if (!isBotCampaign && fieldName === "agentIds") {
          targetElement = document.querySelector(
            '[data-field="assign-agents"]',
          ) as HTMLElement;
        } else if (fieldName === "eligibleLeadStatuses") {
          targetElement = document.querySelector(
            '[data-field="eligible-lead-statuses"]',
          ) as HTMLElement;
        } else {
          // For other fields, look for the input/textarea element
          targetElement = document.querySelector(
            `[name="${fieldName}"]`,
          ) as HTMLElement;
        }

        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          break;
        }
      }
    }
  };

  const onSubmit = async (data: UpdateCampaignFormData) => {
    console.log("Form submission started");
    console.log("Form data:", data);
    console.log("Form errors:", errors);

    try {
      setLoading(true);
      // Convert interestedQuestions from string to array
      const interestedQuestions = data.interestedQuestions
        ? data.interestedQuestions
            .split("\n")
            .map((q) => q.trim())
            .filter((q) => q.length > 0)
        : [];

      let updateData: any;
      if (isActiveCampaign) {
        updateData = {
          name: data.name,
          agentIds: data.agentIds,
          timezonePrioritizationEnabled: data.timezonePrioritizationEnabled,
          areaCodePriorityEnabled: data.areaCodePriorityEnabled,
          callsPerAgent: data.callsPerAgent,
          dialRatio: data.dialRatio,
          dialRatioMin: data.dialRatioMin,
          dialRatioMax: data.dialRatioMax,
          leadShuffleEnabled: data.leadShuffleEnabled,
          dialTimezoneFilter: data.dialTimezoneFilter,
          quietHoursStart: data.quietHoursStart,
          quietHoursEnd: data.quietHoursEnd,
          voiceDialSchedules: data.voiceDialSchedules || [],
          botDialingMode: data.botDialingMode,
          botTargetActiveConversations: data.botTargetActiveConversations,
          botMaxActiveConversations: data.botMaxActiveConversations,
          botWaveSize: data.botWaveSize,
          botWaveIntervalMs: data.botWaveIntervalMs,
          // Safe while live — next outbound picks these up
          assistantFirstMessage: data.assistantFirstMessage,
          assistantFirstMessageMode:
            data.campaignType === "bot"
              ? data.assistantFirstMessageMode || "assistant-speaks-first"
              : "assistant-speaks-first",
          // Next call reads voice from conversation styles (ElevenLabs / Fish).
          conversationStyles: data.conversationStyles,
          // Manual dial script is safe to change while live (agents see it on next call)
          // Skip while scripts are still loading so we don't accidentally clear the assignment
          ...(loadingScripts ? {} : { scriptId: data.scriptId || null }),
        };
      } else if (isAdmin) {
        const {
          sequenceSteps: _steps,
          sendSchedules: _sendSchedules,
          textTemplateBody: _textTemplateBody,
          warmupStartVolume: _warmupStartVolume,
          warmupEndVolume: _warmupEndVolume,
          warmupDays: _warmupDays,
          eligibleLeadStatuses: _eligibleLeadStatuses,
          scriptId: _scriptId,
          ...rest
        } = data;
        updateData = {
          ...rest,
          ...(data.campaignType === "text" ? { dialingType: "auto" as const } : {}),
          interestedQuestions,
          assistantFirstMessage:
            data.campaignType === "human"
              ? data.assistantFirstMessage &&
                data.assistantFirstMessage.trim().length > 0
                ? data.assistantFirstMessage
                : "This call will be handled by a human agent."
              : data.assistantFirstMessage,
          assistantFirstMessageMode:
            data.campaignType === "bot"
              ? data.assistantFirstMessageMode || "assistant-speaks-first"
              : "assistant-speaks-first",
          promptScript:
            data.campaignType === "human"
              ? data.promptScript && data.promptScript.trim().length > 0
                ? data.promptScript
                : "Human agent call."
              : data.campaignType === "text"
                ? "SMS campaign"
              : data.promptScript,
          textConfig:
            data.campaignType === "text"
              ? {
                  templateBody: data.sequenceSteps?.[0]?.templateBody?.trim() || data.textTemplateBody || "",
                  warmupStartVolume: data.warmupStartVolume || 25,
                  warmupEndVolume: data.warmupEndVolume || 250,
                  warmupDays: data.warmupDays || 14,
                  eligibleLeadStatuses: data.eligibleLeadStatuses || [],
                  sendSchedules: data.sendSchedules || [],
                }
              : undefined,
          quietHoursStart: data.campaignType === "text" ? "" : data.quietHoursStart,
          quietHoursEnd: data.campaignType === "text" ? "" : data.quietHoursEnd,
          ...(loadingScripts ? {} : { scriptId: data.scriptId || null }),
        };
      } else {
        updateData = {
          name: data.name,
        };
      }

      // Debug: Log raw form data before Zod validation
      console.log("🔍 Raw form data before Zod validation:", {
        conversationStyles: data.conversationStyles?.map((style) => ({
          name: style.name,
          selectedVoiceId: style.selectedVoiceId,
          voiceConfig: style.voiceConfig,
          voiceConfigType: typeof style.voiceConfig,
          voiceConfigUndefined: style.voiceConfig === undefined,
          voiceConfigNull: style.voiceConfig === null,
        })),
      });

      console.log("Form data being sent:", updateData);
      // Log conversation styles only when present and correctly shaped
      if (
        "conversationStyles" in updateData &&
        Array.isArray((updateData as any).conversationStyles)
      ) {
        console.log(
          "Conversation styles:",
          (updateData as any).conversationStyles,
        );
      }

      // Debug each conversation style
      if (
        "conversationStyles" in updateData &&
        Array.isArray((updateData as any).conversationStyles)
      ) {
        ((updateData as any).conversationStyles as ConversationStyle[]).forEach(
          (style: ConversationStyle, index: number) => {
            console.log(`Style ${index}:`, {
              name: style.name,
              selectedVoiceId: style.selectedVoiceId,
              voiceConfig: style.voiceConfig,
              voiceConfigType: typeof style.voiceConfig,
              isVoiceConfigUndefined: style.voiceConfig === undefined,
              isVoiceConfigNull: style.voiceConfig === null,
              isVoiceConfigEmptyObject:
                style.voiceConfig &&
                typeof style.voiceConfig === "object" &&
                Object.keys(style.voiceConfig).length === 0,
            });
          },
        );
      }

      await campaignService.updateCampaign(id!, updateData);
      if (data.campaignType === "text") {
        const sanitizedSteps = (data.sequenceSteps || [])
          .map((step: any, index: number) => ({
            stepNumber: Number(step.stepNumber || index + 1),
            templateBody: String(step.templateBody || "").trim(),
            delayHours: Number(step.delayHours || 0),
          }))
          .filter((step: any) => step.templateBody.length > 0);

        const fallbackStep =
          sanitizedSteps.length > 0
            ? sanitizedSteps
            : [
                {
                  stepNumber: 1,
                  templateBody: String(data.textTemplateBody || "").trim(),
                  delayHours: 0,
                },
              ].filter((step) => step.templateBody.length > 0);

        await smsService.updateSequenceSteps(id!, fallbackStep);
      }
      toast.success("Campaign updated successfully!");
      navigate(`/campaigns/${id}`);
    } catch (error: any) {
      console.error("Failed to update campaign:", error);

      toast.error(formatApiErrorMessage(error, "Failed to update campaign"));

      // Scroll to first error field after a short delay
      setTimeout(() => {
        scrollToError();
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  // Map form fields to their sections for auto-navigation on validation errors
  const fieldToSection: Record<string, string> = {
    name: "basic-info",
    callDescription: "basic-info",
    timezone: "basic-info",
    campaignType: "basic-info",
    agentIds: "assign-agents",
    promptScript: "script-config",
    assistantFirstMessage: "script-config",
    interestedQuestions: "script-config",
    phoneNumberIds: "script-config",
    telnyxAssistantId: "script-config",
    conversationStyles: "conversation-styles",
    conversationStyle: "conversation-styles",
    personalityType: "conversation-styles",
    persuasionApproach: "conversation-styles",
    captureSchema: "data-capture",
  };

  const onInvalidSubmit = (formErrors: any) => {
    console.log("Form validation failed");
    console.log("Validation errors:", formErrors);
    console.log("Current form values:", watch());

    // Find the first error field and its section
    const errorFieldNames = Object.keys(formErrors);
    const errorMessages = errorFieldNames.map((f) => {
      const err = formErrors[f];
      return err?.message || f;
    });

    // Show specific error messages in toast
    if (errorMessages.length > 0) {
      toast.error(`Validation errors: ${errorMessages.join(", ")}`);
    } else {
      toast.error("Please fix the validation errors before submitting");
    }

    // Auto-navigate to the section containing the first error
    if (errorFieldNames.length > 0) {
      const firstErrorField = errorFieldNames[0];
      const targetSection = fieldToSection[firstErrorField];
      if (targetSection && targetSection !== activeSection) {
        setActiveSection(targetSection);
      }
    }

    // Scroll to first error field after section switch
    setTimeout(() => {
      scrollToError();
    }, 150);
  };

  const addCaptureField = () => {
    const newField: CaptureField = {
      name: "",
      label: "",
      type: "string",
      required: false,
      placeholder: "",
    };
    setValue("captureSchema", [...safeCaptureSchema, newField]);
  };

  const removeCaptureField = (index: number) => {
    const updatedSchema = safeCaptureSchema.filter((_, i) => i !== index);
    setValue("captureSchema", updatedSchema);
  };

  const updateCaptureField = (index: number, field: Partial<CaptureField>) => {
    const updatedSchema = safeCaptureSchema.map((item, i) =>
      i === index ? { ...item, ...field } : item,
    );
    setValue("captureSchema", updatedSchema);
  };

  if (pageLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-foreground">
          Campaign not found
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          The campaign you're trying to edit doesn't exist.
        </p>
        <div className="mt-6">
          <Button onClick={() => navigate("/campaigns")} variant="primary">
            Back to Campaigns
          </Button>
        </div>
      </div>
    );
  }

  if (isRestricted) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-foreground">
          Insufficient Permissions
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Your account is restricted. You can view campaigns and results, but
          cannot create or edit campaigns or leads. Please contact your
          administrator if you need elevated access.
        </p>
        <div className="mt-6">
          <Button
            onClick={() => navigate(`/campaigns/${id}`)}
            variant="primary"
          >
            View Campaign
          </Button>
        </div>
      </div>
    );
  }

  const isActiveCampaign = campaign.status === "active";

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: "Campaigns", href: "/campaigns" },
          { label: campaign.name, href: `/campaigns/${id}` },
          { label: "Edit", current: true },
        ]}
      />

      {/* Header */}
      <Card className="p-6 mb-8 mt-6">
        <CardContent className="p-0">
          <h1 className="text-2xl font-bold mb-2 text-foreground">
            Edit Campaign
          </h1>
          <p className="text-muted-foreground">
            Update your campaign settings and configuration.
          </p>
        </CardContent>
      </Card>

      <div className="flex gap-8">
        <div className="w-80 flex-shrink-0">
          <CampaignSideNav
            sections={
              isAdmin
                ? [
                    {
                      id: "basic-info",
                      title: "Basic Information",
                      icon: <DocumentTextIcon className="h-4 w-4" />,
                    },
                    {
                      id: "assign-agents",
                      title: isBotCampaign
                        ? "Assign Agents (pipeline)"
                        : "Assign Agents",
                      icon: <UserGroupIcon className="h-4 w-4" />,
                    },
                    ...(isBotCampaign
                      ? [
                          {
                            id: "script-config",
                            title: "Script Configuration",
                            icon: <CogIcon className="h-4 w-4" />,
                          },
                          {
                            id: "conversation-styles",
                            title: "Conversation Styles",
                            icon: (
                              <ChatBubbleLeftRightIcon className="h-4 w-4" />
                            ),
                          },
                          {
                            id: "data-capture",
                            title: "Data Capture Schema",
                            icon: <TableCellsIcon className="h-4 w-4" />,
                          },
                        ]
                      : []),
                  ]
                : [
                    {
                      id: "basic-info",
                      title: "Basic Information",
                      icon: <DocumentTextIcon className="h-4 w-4" />,
                    },
                  ]
            }
            activeSection={activeSection}
            onSectionChange={setActiveSection}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <form
            onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}
            className="space-y-8"
          >
            {isActiveCampaign && (
              <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3 mb-4">
                <p className="text-sm text-yellow-400">
                  Campaign is active. You can change agents, name, dial settings, quiet hours, and the manual dial script. Pause the campaign to edit script text, voices, and other core settings.
                </p>
              </div>
            )}

            {/* Basic Information */}
            {activeSection === "basic-info" && (
              <Card>
                <CardContent className="px-4 py-5 sm:p-6">
                  <SectionHeader title="Basic Information" className="mb-4" />

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-foreground"
                      >
                        Campaign Name *
                      </label>
                      <input
                        type="text"
                        {...register("name")}
                        className="input"
                        placeholder="Enter campaign name"
                      />
                      <ErrorMessage message={errors.name?.message} />
                    </div>

                    {isAdmin && !isTextCampaign && (
                      <div>
                        <label
                          htmlFor="timezone"
                          className="block text-sm font-medium text-foreground"
                        >
                          Timezone *
                        </label>
                        <select {...register("timezone")} className="select" disabled={isActiveCampaign}>
                          {CAMPAIGN_TIMEZONE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <ErrorMessage message={errors.timezone?.message} />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        Campaign Type
                      </label>
                      <select {...register("campaignType")} className="select" disabled={isActiveCampaign}>
                        <option value="human">Human Calling</option>
                        <option value="bot">AI Calling</option>
                        <option value="text">Text (SMS)</option>
                      </select>
                    </div>

                    {/* Dialing Type selector — human campaigns only; bot uses auto-dial */}
                    {!isTextCampaign && !isBotCampaign && <div>
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        Dialing Mode
                      </label>
                      <div className="flex gap-3">
                        <label className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                          watch('dialingType') === 'auto'
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-muted-foreground/30'
                        }`}>
                          <input
                            type="radio"
                            value="auto"
                            {...register('dialingType')}
                            className="accent-primary"
                            disabled={isActiveCampaign}
                          />
                          <div>
                            <div className="font-medium text-sm">Auto Dialer</div>
                            <div className="text-xs text-muted-foreground">System automatically dials leads</div>
                          </div>
                        </label>
                        <label className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                          watch('dialingType') === 'manual'
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-muted-foreground/30'
                        }`}>
                          <input
                            type="radio"
                            value="manual"
                            {...register('dialingType')}
                            className="accent-primary"
                            disabled={isActiveCampaign}
                          />
                          <div>
                            <div className="font-medium text-sm">Manual Dial</div>
                            <div className="text-xs text-muted-foreground">Agents choose which leads to call</div>
                          </div>
                        </label>
                      </div>
                    </div>}

                    {isBotCampaign && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        AI campaigns automatically dial leads from the queue — no agent required.
                      </p>
                    )}
                  </div>

                  {isAdmin && !isTextCampaign && (
                    <div className="mt-6 space-y-3">
                      <Controller
                        name="timezonePrioritizationEnabled"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            checked={!!field.value}
                            onChange={(event) => field.onChange(event.target.checked)}
                            label="Enable timezone-based lead filtering"
                            description="When ON, only leads in the current allowed timezone window are dialed. When OFF, leads from all timezones are eligible."
                          />
                        )}
                      />
                      <Controller
                        name="areaCodePriorityEnabled"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            checked={!!field.value}
                            onChange={(event) => field.onChange(event.target.checked)}
                            label="Prioritize leads by area-code pickup performance"
                            description="Sort eligible leads by highest pickup rate first."
                          />
                        )}
                      />
                      {watch('dialingType') === 'auto' && (
                        <>
                          <div>
                            <label htmlFor="dialRatio" className="block text-sm font-semibold text-foreground mb-2">
                              Dial ratio (adaptive pacing)
                            </label>
                            <input
                              id="dialRatio"
                              type="number"
                              min={1}
                              max={2.5}
                              step={0.1}
                              {...register('dialRatio', {
                                setValueAs: (v) => (v === '' || v === null || Number.isNaN(Number(v)) ? null : Number(v)),
                              })}
                              className="input max-w-[120px]"
                              placeholder="1.3"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Target active calls = available agents × ratio (e.g. 7 agents × 1.3 ≈ 9).
                            </p>
                          </div>
                          <div>
                            <label htmlFor="dialTimezoneFilter" className="block text-sm font-semibold text-foreground mb-2">
                              Lead timezone filter
                            </label>
                            <select id="dialTimezoneFilter" {...register('dialTimezoneFilter')} className="select max-w-xs">
                              <option value="auto">Auto (ET wave or campaign timezone)</option>
                              <option value="Eastern">Eastern</option>
                              <option value="Central">Central</option>
                              <option value="Mountain">Mountain</option>
                              <option value="Pacific">Pacific</option>
                              <option value="Unknown">Unknown</option>
                            </select>
                          </div>
                          <Controller
                            name="leadShuffleEnabled"
                            control={control}
                            render={({ field }) => (
                              <Switch
                                checked={!!field.value}
                                onChange={(event) => field.onChange(event.target.checked)}
                                label="Shuffle lead dial order"
                                description="Randomize eligible leads each scheduler cycle."
                              />
                            )}
                          />
                        </>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                        <div>
                          <label htmlFor="quietHoursStart" className="block text-sm font-semibold text-foreground mb-2">
                            Quiet hours start
                          </label>
                          <input id="quietHoursStart" type="time" {...register('quietHoursStart')} className="input" />
                        </div>
                        <div>
                          <label htmlFor="quietHoursEnd" className="block text-sm font-semibold text-foreground mb-2">
                            Quiet hours end
                          </label>
                          <input id="quietHoursEnd" type="time" {...register('quietHoursEnd')} className="input" />
                        </div>
                      </div>
                      {(isBotCampaign || (isHumanCampaign && watch('dialingType') === 'auto')) && (
                        <div className="pt-4">
                          <DialScheduleFields
                            schedules={watch('voiceDialSchedules') || []}
                            onChange={(schedules) => setValue('voiceDialSchedules', schedules)}
                            error={errors.voiceDialSchedules?.message as string | undefined}
                            disabled={false}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {isAdmin && isTextCampaign && (
                    <div className="mt-6">
                      <SmsCampaignSetupFields
                        sequenceSteps={sequenceSteps}
                        onSequenceStepsChange={(steps) => setValue("sequenceSteps", steps)}
                        onFirstStepMessageChange={(body) => setValue("textTemplateBody", body)}
                        phoneNumberIds={watch("phoneNumberIds") || []}
                        onPhoneNumberIdsChange={(ids) => setValue("phoneNumberIds", ids)}
                        displayPhoneNumbers={displayPhoneNumbers}
                        loadingPhoneNumbers={loadingPhoneNumbers}
                        phoneNumberError={errors.phoneNumberIds?.message as string | undefined}
                        phoneNumbersEmptyHint={phoneLoadError || undefined}
                        sequenceStepsError={errors.sequenceSteps?.message as string | undefined}
                        warmupStartVolume={watch("warmupStartVolume") ?? 25}
                        warmupEndVolume={watch("warmupEndVolume") ?? 250}
                        warmupDays={watch("warmupDays") ?? 14}
                        onWarmupChange={(field, value) => setValue(field, value)}
                        sendSchedules={watch("sendSchedules") || []}
                        onSendSchedulesChange={(schedules) => setValue("sendSchedules", schedules)}
                        sendSchedulesError={errors.sendSchedules?.message as string | undefined}
                        sendSchedulesDisabled={isActiveCampaign}
                        eligibleLeadStatuses={eligibleLeadStatuses}
                        onEligibleLeadStatusesChange={(statuses) => setValue("eligibleLeadStatuses", statuses)}
                        eligibleLeadStatusesError={errors.eligibleLeadStatuses?.message as string | undefined}
                        disabled={isActiveCampaign}
                      />
                    </div>
                  )}

                  {isAdmin && (
                    <div className="mt-6">
                      <label
                        htmlFor="callDescription"
                        className="block text-sm font-medium text-foreground"
                      >
                        {isTextCampaign ? "Campaign notes (internal)" : "Call Description"}
                      </label>
                      <textarea
                        {...register("callDescription")}
                        rows={3}
                        className="textarea"
                        placeholder={
                          isTextCampaign
                            ? "e.g. Q2 re-engagement SMS for inbound form leads"
                            : "Describe what this campaign is about..."
                        }
                      />
                      {isTextCampaign && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          For your team only — not sent to leads as an SMS.
                        </p>
                      )}
                      <ErrorMessage message={errors.callDescription?.message} />
                    </div>
                  )}

                  {isAdmin && !isTextCampaign && (
                    <div className="mt-6">
                      <label
                        htmlFor="scriptId"
                        className="block text-sm font-medium text-foreground"
                      >
                        Manual Dial Script
                      </label>
                      <Controller
                        name="scriptId"
                        control={control}
                        render={({ field }) => {
                          const selectedId = field.value || "";
                          const options = scripts.filter(
                            (script) =>
                              script.isActive || script.id === selectedId,
                          );
                          const missingAssigned =
                            !!selectedId &&
                            !scripts.some((script) => script.id === selectedId);

                          return (
                            <select
                              id="scriptId"
                              className="select"
                              disabled={loadingScripts}
                              value={selectedId}
                              onBlur={field.onBlur}
                              ref={field.ref}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value === "" ? null : e.target.value,
                                )
                              }
                            >
                              <option value="">
                                {loadingScripts
                                  ? "Loading scripts..."
                                  : "No script assigned"}
                              </option>
                              {missingAssigned && (
                                <option value={selectedId}>
                                  Current script (not in your list)
                                </option>
                              )}
                              {options.map((script) => (
                                <option key={script.id} value={script.id}>
                                  {script.name}
                                  {!script.isActive ? " (inactive)" : ""}
                                </option>
                              ))}
                            </select>
                          );
                        }}
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        This script is shown to agents in the in-call popup during manual calls.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Assign Agents (human dialing + AI pipeline routing) */}
            {isAdmin && activeSection === "assign-agents" && (
              <Card>
                <CardContent className="px-4 py-5 sm:p-6">
                  <SectionHeader
                    title={
                      isBotCampaign
                        ? "Assign Agents (pipeline)"
                        : "Assign Agents"
                    }
                    className="mb-4"
                  />

                  <div className="space-y-6" data-field="assign-agents">
                    <div>
                      <label className="block text-sm font-medium text-foreground">
                        Select Agents{isBotCampaign ? "" : " *"}
                      </label>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {isBotCampaign
                          ? "Optional: choose agents who get pipeline leads (Interested, Follow Up, App In, App Out) and live Bot Monitor access for this AI campaign."
                          : "Choose which agents will handle calls for this campaign."}
                      </p>

                      {loadingAgents ? (
                        <div className="mt-3 flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                          <span className="ml-2 text-sm text-muted-foreground">Loading agents...</span>
                        </div>
                      ) : agents.length === 0 ? (
                        <div className="mt-3 p-4 bg-muted rounded-lg border border-border">
                          <p className="text-sm text-muted-foreground">No agents available. Please create agents first in the Admin panel.</p>
                        </div>
                      ) : (
                        <Controller
                          name="agentIds"
                          control={control}
                          render={({ field }) => {
                            const selectedIds = field.value || [];
                            const filteredAgents = agents.filter(a =>
                              (a.name || '').toLowerCase().includes(agentSearch.toLowerCase()) ||
                              a.email.toLowerCase().includes(agentSearch.toLowerCase())
                            );
                            const allSelected = agents.length > 0 && selectedIds.length === agents.length;
                            const someSelected = selectedIds.length > 0 && selectedIds.length < agents.length;

                            const handleToggleAgent = (agentId: string) => {
                              if (selectedIds.includes(agentId)) {
                                field.onChange(selectedIds.filter((id: string) => id !== agentId));
                              } else {
                                field.onChange([...selectedIds, agentId]);
                              }
                            };

                            const handleSelectAll = () => {
                              if (allSelected) {
                                field.onChange([]);
                              } else {
                                field.onChange(agents.map(a => a.id));
                              }
                            };

                            return (
                              <div className="mt-2">
                                {/* Select All Header */}
                                <div className="flex items-center justify-between p-3 bg-muted border border-border rounded-t-lg">
                                  <label className="flex items-center cursor-pointer">
                                    <div className="relative">
                                      <input
                                        type="checkbox"
                                        checked={allSelected}
                                        ref={(input) => {
                                          if (input) input.indeterminate = someSelected;
                                        }}
                                        onChange={handleSelectAll}
                                        className="sr-only"
                                      />
                                      <div className={`w-5 h-5 rounded-md border-2 transition-all duration-200 cursor-pointer flex items-center justify-center ${
                                        allSelected
                                          ? 'bg-primary border-primary shadow-sm'
                                          : someSelected
                                          ? 'bg-primary border-primary shadow-sm'
                                          : 'border-border hover:border-primary/60 bg-card'
                                      }`}>
                                        {allSelected && (
                                          <svg className="w-3 h-3 text-primary-foreground" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                        )}
                                        {someSelected && !allSelected && (
                                          <div className="w-2 h-2 bg-primary-foreground rounded-sm"></div>
                                        )}
                                      </div>
                                    </div>
                                    <span className="ml-3 text-sm font-medium text-foreground">
                                      {allSelected ? 'Unselect All' : 'Select All'}
                                    </span>
                                  </label>
                                  <span className="text-xs text-muted-foreground">
                                    {selectedIds.length} of {agents.length} selected
                                  </span>
                                </div>

                                {/* Search Agents */}
                                <div className="px-3 py-2 border border-t-0 border-border bg-card">
                                  <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <input
                                      type="text"
                                      placeholder="Search agents by name or email..."
                                      value={agentSearch}
                                      onChange={(e) => setAgentSearch(e.target.value)}
                                      className="input pl-8 py-1.5 text-sm w-full"
                                    />
                                  </div>
                                </div>

                                {/* Agent List */}
                                <div className="border border-t-0 border-border rounded-b-lg max-h-64 overflow-y-auto">
                                  {filteredAgents.length === 0 ? (
                                    <div className="p-4 text-center text-sm text-muted-foreground">
                                      No agents match "{agentSearch}"
                                    </div>
                                  ) : filteredAgents.map((agent, index) => {
                                    const isSelected = selectedIds.includes(agent.id);
                                    return (
                                      <div
                                        key={agent.id}
                                        className={`flex items-center justify-between p-3 cursor-pointer transition-colors duration-150 ${
                                          isSelected ? 'bg-primary/5' : 'hover:bg-muted/50'
                                        } ${index < filteredAgents.length - 1 ? 'border-b border-border' : ''}`}
                                        onClick={() => handleToggleAgent(agent.id)}
                                      >
                                        <div className="flex items-center min-w-0 flex-1">
                                          <div className="relative">
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              onChange={() => handleToggleAgent(agent.id)}
                                              className="sr-only"
                                            />
                                            <div className={`w-5 h-5 rounded-md border-2 transition-all duration-200 cursor-pointer flex items-center justify-center ${
                                              isSelected
                                                ? 'bg-primary border-primary shadow-sm'
                                                : 'border-border hover:border-primary/60 bg-card'
                                            }`}>
                                              {isSelected && (
                                                <svg className="w-3 h-3 text-primary-foreground" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              )}
                                            </div>
                                          </div>
                                          <div className="ml-3 min-w-0">
                                            <p className={`text-sm font-medium truncate ${
                                              isSelected ? 'text-foreground' : 'text-muted-foreground'
                                            }`}>
                                              {agent.name || agent.email}
                                            </p>
                                            {agent.name && (
                                              <p className="text-xs text-muted-foreground truncate">
                                                {agent.email}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                        <div className="ml-2 flex-shrink-0">
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                            agent.phone_number_provider
                                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                          }`}>
                                            {agent.phone_number_provider ? 'WebRTC Ready' : 'No WebRTC'}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          }}
                        />
                      )}
                      <ErrorMessage message={errors.agentIds?.message} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Conversation Styles */}
            {isAdmin &&
              isBotCampaign &&
              activeSection === "conversation-styles" && (
                <Card>
                  <CardContent className="px-4 py-5 sm:p-6">
                    <SectionHeader
                      title="Conversation Styles"
                      className="mb-4"
                    />
                    <p className="text-sm text-muted-foreground mb-6">
                      Set Bot Engine in Script Configuration first — then pick
                      the matching voice here. The AI randomly uses one active
                      style per call.
                    </p>

                    <Controller
                      name="conversationStyles"
                      control={control}
                      render={({ field }) => (
                        <ConversationStylesManager
                          conversationStyles={field.value || []}
                          onChange={field.onChange}
                          campaignId={id}
                          onGeneratingChange={setIsGeneratingPrompt}
                          botEngine={botEngine}
                        />
                      )}
                    />
                  </CardContent>
                </Card>
              )}

            {/* Script Configuration */}
            {isAdmin &&
              isBotCampaign &&
              activeSection === "script-config" && (
                <Card>
                  <CardContent className="px-4 py-5 sm:p-6">
                    <SectionHeader
                      title="Script Configuration"
                      className="mb-4"
                    />

                    <div className="space-y-6">
                      {/* Bot engine */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Bot Engine
                        </label>
                        <p className="text-xs text-muted-foreground mb-2">
                          Choose engine first — Conversation Styles voices follow this (Telnyx NaturalHD, ElevenLabs, or Fish Audio on self-hosted).
                        </p>
                        <div className="space-y-2">
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              value="telnyx_assistant"
                              {...register("botEngine")}
                              className="mt-1"
                            />
                            <span>
                              <span className="font-medium text-foreground">Telnyx AI Assistant</span>
                              <span className="ml-2 text-xs text-violet-600 dark:text-violet-400">Recommended</span>
                              <p className="text-sm text-muted-foreground">
                                Managed voice AI — stable back-and-forth, no custom WebSocket.
                              </p>
                            </span>
                          </label>
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              value="conversation_relay"
                              {...register("botEngine")}
                              className="mt-1"
                            />
                            <span>
                              <span className="font-medium text-foreground">Custom AI (OpenAI)</span>
                              <p className="text-sm text-muted-foreground">
                                Conversation Relay + your OpenAI key — advanced control.
                              </p>
                            </span>
                          </label>
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              value="vapi_direct"
                              {...register("botEngine")}
                              className="mt-1"
                            />
                            <span>
                              <span className="font-medium text-foreground">VAPI Direct</span>
                              <p className="text-sm text-muted-foreground">
                                VAPI dials via Telnyx numbers imported in VAPI.
                              </p>
                            </span>
                          </label>
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              value="vapi_bridge"
                              {...register("botEngine")}
                              className="mt-1"
                            />
                            <span>
                              <span className="font-medium text-foreground">VAPI Bridge</span>
                              <p className="text-sm text-muted-foreground">
                                Telnyx Call Control dial, then bridge to VAPI on answer.
                                Requires server env <code className="text-xs">VAPI_BRIDGE_TARGET</code>;
                                without it the call answers then hangs up immediately.
                              </p>
                            </span>
                          </label>
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              value="self_hosted"
                              {...register("botEngine")}
                              className="mt-1"
                            />
                            <span>
                              <span className="font-medium text-foreground">Full Custom AI (self-hosted)</span>
                              <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">Experimental</span>
                              <p className="text-sm text-muted-foreground">
                                Our own STT + TTS over Telnyx media streaming — no Conversation Relay
                                fee. Full conversations are implemented but have not yet run on a
                                live call. Behaviour depends on the server&apos;s
                                <code className="mx-1">SELF_HOSTED_BOT_MODE</code>: <em>smoke</em>{' '}
                                speaks one line and hangs up, <em>listen</em> adds transcription,{' '}
                                <em>full</em> holds a conversation. Test on your own number first.
                              </p>
                            </span>
                          </label>
                        </div>
                      </div>

                      {isConversationRelayBot && (
                        <div className="rounded-md border border-border bg-muted/30 px-4 py-3">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register("botPipelineV2")}
                              className="mt-1 h-4 w-4 rounded border-border"
                            />
                            <span>
                              <span className="font-medium text-foreground">
                                Human voice pipeline (v2)
                              </span>
                              <p className="text-sm text-muted-foreground mt-1">
                                ElevenLabs TTS + Deepgram STT + stage engine. Requires platform
                                ElevenLabs secret on Telnyx. Pick an ElevenLabs voice in Conversation
                                Styles below.
                              </p>
                            </span>
                          </label>
                        </div>
                      )}

                      {isBotCampaign && isAdmin && (
                        <div className="rounded-md border border-border bg-muted/20 px-4 py-4 space-y-4">
                          <div>
                            <h4 className="text-sm font-semibold text-foreground">
                              Capacity dialer (AI conversations)
                            </h4>
                            <p className="text-xs text-muted-foreground mt-1">
                              Wave-based dialing to maintain a target number of active AI conversations.
                              Requires <code className="text-xs">BOT_CAPACITY_DIALER</code> env on the server.
                              Human auto-dialer is unaffected.
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                              Dialing mode
                            </label>
                            <select {...register('botDialingMode')} className="select max-w-xs">
                              <option value="legacy">Legacy (fixed batch)</option>
                              <option value="capacity">Capacity (active-conversation target)</option>
                            </select>
                          </div>
                          {watch('botDialingMode') === 'capacity' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                  Target active AI conversations
                                </label>
                                <input
                                  type="number"
                                  min={1}
                                  max={100}
                                  {...register('botTargetActiveConversations', {
                                    setValueAs: (v) =>
                                      v === '' || Number.isNaN(Number(v)) ? undefined : Number(v),
                                  })}
                                  className="input"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                  Max active (hard ceiling)
                                </label>
                                <input
                                  type="number"
                                  min={1}
                                  max={150}
                                  {...register('botMaxActiveConversations', {
                                    setValueAs: (v) =>
                                      v === '' || Number.isNaN(Number(v)) ? undefined : Number(v),
                                  })}
                                  className="input"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                  Wave size
                                </label>
                                <input
                                  type="number"
                                  min={1}
                                  max={100}
                                  {...register('botWaveSize', {
                                    setValueAs: (v) =>
                                      v === '' || Number.isNaN(Number(v)) ? undefined : Number(v),
                                  })}
                                  className="input"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                  Wave interval (ms)
                                </label>
                                <input
                                  type="number"
                                  min={500}
                                  max={10000}
                                  step={100}
                                  {...register('botWaveIntervalMs', {
                                    setValueAs: (v) =>
                                      v === '' || Number.isNaN(Number(v)) ? undefined : Number(v),
                                  })}
                                  className="input"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {isVapiDirectBot && (
                        <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                          Outbound caller IDs are chosen automatically from{" "}
                          <span className="font-medium text-foreground">
                            Manage Numbers
                          </span>
                          {" "}
                          — active numbers with purpose{" "}
                          <span className="font-medium text-foreground">
                            AI Caller
                          </span>{" "}
                          that are pushed to Vapi. No per-campaign selection
                          needed.
                        </div>
                      )}

                      {isVapiBridgeBot && (
                        <div data-field="phone-numbers">
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Telnyx Outbound Numbers
                          </label>
                          <p className="text-xs text-muted-foreground mb-2">
                            Select Telnyx outbound lines used for bridge
                            dialing.
                          </p>
                          <Controller
                            name="phoneNumberIds"
                            control={control}
                            render={({ field }) => (
                              <PhoneNumberList
                                phoneNumbers={displayPhoneNumbers}
                                selectedPhoneNumberIds={field.value || []}
                                onSelectionChange={field.onChange}
                                loading={loadingPhoneNumbers}
                                error={errors.phoneNumberIds?.message}
                                emptyHint={phoneLoadError || undefined}
                              />
                            )}
                          />
                        </div>
                      )}

                      {isTelnyxAssistantBot && (
                        <Controller
                          name="telnyxAssistantId"
                          control={control}
                          render={({ field }) => (
                            <TelnyxAssistantPicker
                              inputId="telnyxAssistantId"
                              value={field.value || ""}
                              onChange={field.onChange}
                              error={errors.telnyxAssistantId?.message}
                            />
                          )}
                        />
                      )}

                      {/* First Message Override */}
                      <div>
                        <label className="block text-sm font-medium text-foreground">
                          First Message (Greetings) *
                        </label>
                        <textarea
                          {...register("assistantFirstMessage")}
                          rows={3}
                          className="textarea"
                          placeholder="Enter the greeting message for customers when the call starts"
                        />
                        <ErrorMessage
                          message={errors.assistantFirstMessage?.message}
                        />
                        <PlaceholderHelper className="mt-3" />
                      </div>

                      {watch("campaignType") === "bot" && (
                        <div>
                          <label
                            htmlFor="assistantFirstMessageMode"
                            className="block text-sm font-medium text-foreground"
                          >
                            Who speaks first
                          </label>
                          <select
                            id="assistantFirstMessageMode"
                            {...register("assistantFirstMessageMode")}
                            className="input"
                          >
                            <option value="assistant-speaks-first">
                              AI greets first
                            </option>
                            <option value="assistant-waits-for-user">
                              Wait for client to speak first
                            </option>
                          </select>
                          <p className="mt-2 text-sm text-muted-foreground">
                            Use wait mode to test whether the client saying
                            “hello” first improves the conversation.
                          </p>
                        </div>
                      )}

                      <div>
                        <label
                          htmlFor="promptScript"
                          className="block text-sm font-medium text-foreground"
                        >
                          Main Script *
                        </label>
                        <textarea
                          {...register("promptScript")}
                          rows={8}
                          className="textarea"
                          placeholder="Enter the script that the AI will use during calls..."
                        />
                        <ErrorMessage message={errors.promptScript?.message} />
                        <PlaceholderHelper className="mt-3" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

            {/* Data Capture Schema */}
            {isAdmin &&
              isBotCampaign &&
              activeSection === "data-capture" && (
                <Card>
                  <CardContent className="px-4 py-5 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <SectionHeader title="Data Capture Schema" />
                        <p className="mt-1 text-sm text-muted-foreground">
                          Define what information to collect from interested
                          leads
                        </p>
                      </div>
                      <Button
                        type="button"
                        onClick={addCaptureField}
                        variant="outlined"
                        size="sm"
                      >
                        <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
                        Add Field
                      </Button>
                    </div>

                    {safeCaptureSchema.length === 0 ? (
                      <div className="text-center py-6 border-2 border-dashed border-border rounded-lg">
                        <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <InformationCircleIcon className="h-6 w-6 text-primary" />
                        </div>
                        <p className="mt-2 text-sm text-muted-foreground">
                          No capture fields defined. Add fields to collect
                          specific information from interested leads.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {safeCaptureSchema.map((field, index) => (
                          <div
                            key={index}
                            className="border border-border rounded-lg p-4"
                          >
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                <div>
                                  <label className="block text-sm font-medium text-foreground">
                                    Field Name *
                                  </label>
                                  <input
                                    type="text"
                                    value={field.name}
                                    onChange={(e) =>
                                      updateCaptureField(index, {
                                        name: e.target.value,
                                      })
                                    }
                                    className="input"
                                    placeholder="e.g., company_name"
                                  />
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-foreground">
                                    Field Label *
                                  </label>
                                  <input
                                    type="text"
                                    value={field.label}
                                    onChange={(e) =>
                                      updateCaptureField(index, {
                                        label: e.target.value,
                                      })
                                    }
                                    className="input"
                                    placeholder="e.g., Company Name"
                                  />
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-foreground">
                                    Field Type *
                                  </label>
                                  <select
                                    value={field.type}
                                    onChange={(e) =>
                                      updateCaptureField(index, {
                                        type: e.target
                                          .value as CaptureField["type"],
                                      })
                                    }
                                    className="select"
                                  >
                                    <option value="string">Text</option>
                                    <option value="email">Email</option>
                                    <option value="number">Number</option>
                                    <option value="boolean">Yes/No</option>
                                  </select>
                                </div>
                              </div>

                              <div className="flex items-center justify-between">
                                <div className="switch-group">
                                  <Switch
                                    checked={field.required}
                                    onChange={(event) =>
                                      updateCaptureField(index, {
                                        required: event.target.checked,
                                      })
                                    }
                                    label="Required"
                                  />
                                </div>

                                <Button
                                  type="button"
                                  onClick={() => removeCaptureField(index)}
                                  variant="destructive"
                                  size="sm"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>

                            <div className="mt-4">
                              <label className="block text-sm font-medium text-foreground">
                                Description
                              </label>
                              <input
                                type="text"
                                value={field.placeholder || ""}
                                onChange={(e) =>
                                  updateCaptureField(index, {
                                    placeholder: e.target.value,
                                  })
                                }
                                className="input"
                                placeholder="Optional description for this field"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={() => navigate(`/campaigns/${id}`)}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={loading}
                disabled={loading || isGeneratingPrompt}
              >
                Update Campaign
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditCampaignPage;
